import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  Percent,
  ChevronRight,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import api from "../hooks/api";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import { API_URLS } from "../App";
import {
  Campaign,
  CampaignItem,
  DiscountTarget,
  ResellerOption,
} from "../model/Campaign";
import CampaignItemErpPanel from "../components/Campaigns/CampaignItemErpPanel";
import CampaignHistoryTab from "../components/Campaigns/CampaignHistoryTab";
import ResellerCombobox from "../components/Campaigns/ResellerCombobox";
import {
  InvalidatedBadge,
  OtherCampaigns,
  RevalidateButton,
} from "../components/Campaigns/CampaignItemStatus";
import {
  buildCampaignItemRequest,
  campaignItemToFormState,
  CampaignItemRequestBody,
} from "../helpers/campaignItem";
import { eur } from "../helpers/format";

interface SearchResultCompound {
  id: string;
  name: string;
  image?: string;
  price: {
    value: number;
    cost?: number | null;
    resell?: number | null;
    pvp_without_vat?: number | null;
    without_vat?: number | null;
    vat_rate?: number | null;
  };
}

const PROMO_TYPE_LABELS: Record<string, string> = {
  DISCOUNT_PERCENTAGE: "Desconto (%)",
  DISCOUNT_VALUE: "Desconto (€)",
  FIXED_PVP: "PVP Fixo",
};

const DISCOUNT_TARGET_LABELS: Record<string, string> = {
  NIPOSOM_MARGIN: "Niposom",
  RESELLER_MARGIN: "Reseller",
  BOTH: "Ambas",
};

const CampaignDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { setLoading, setError, setSuccess } = useLayoutContext();

  const isEditMode = !!id;
  const isEditing = searchParams.get("edit") === "true" || !isEditMode;

  const [profileData, setProfileData] = useState<ProfileAndSettings | null>(
    null,
  );
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [resellerErpRef, setResellerErpRef] = useState("");
  const [resellers, setResellers] = useState<ResellerOption[]>([]);
  const [isGlobal, setIsGlobal] = useState(false);
  const [items, setItems] = useState<CampaignItem[]>([]);
  const [editingItem, setEditingItem] = useState<CampaignItem | null>(null);
  const [preview, setPreview] = useState<CampaignItem | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "history">("details");
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPending, setPreviewPending] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultCompound[]>(
    [],
  );
  const [selectedCompoundId, setSelectedCompoundId] = useState<string | null>(
    null,
  );
  const [newPromoType, setNewPromoType] = useState<string>(
    "DISCOUNT_PERCENTAGE",
  );
  const [newDiscountPct, setNewDiscountPct] = useState<number | null>(null);
  const [newDiscountValue, setNewDiscountValue] = useState<number | null>(null);
  const [newFixedPvp, setNewFixedPvp] = useState<number | null>(null);
  const [newDiscountTarget, setNewDiscountTarget] =
    useState<string>("RESELLER_MARGIN");
  const [newNiposomSplitPct, setNewNiposomSplitPct] = useState<number>(50);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [revalidatePendingId, setRevalidatePendingId] = useState<number | null>(
    null,
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const modalTriggerRef = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (showAddModal) {
      // Skip the sr-only target radios so focus lands on a visible control.
      modalRef.current
        ?.querySelector<HTMLElement>(
          'input[type="number"], input[type="text"], button',
        )
        ?.focus();
    }
  }, [showAddModal]);

  const loadProfile = async () => {
    try {
      const p = await profile();
      setProfileData(p);
    } catch (err) {
      console.warn("Failed to load profile:", err);
    }
  };

  const loadCampaign = async () => {
    if (!id) return;
    setLoading({ message: "A carregar campanha..." });
    try {
      const response = await api.get(`${API_URLS.CAMPAIGNS}/${id}`);
      const data = response.data as Campaign;
      setCampaign(data);
      setName(data.name);
      setDescription(data.description || "");
      setStartsAt(formatDateForInput(data.starts_at));
      setEndsAt(formatDateForInput(data.ends_at));
      setResellerErpRef(data.reseller_erp_ref || "");
      setIsGlobal(data.is_global);
      setItems(data.items || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar campanha");
    } finally {
      setLoading(null);
    }
  };

  const formatDateForInput = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const toLocalInput = (d: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Earliest allowed start/end: "now" (campaigns cannot be created in the past).
  const minStart = toLocalInput(new Date());
  const minEnd = startsAt || minStart;

  const canEdit =
    profileData?.isAdmin() ||
    (profileData?.isMainAccount() && !campaign?.is_global);

  useEffect(() => {
    loadProfile();
    if (isEditMode) {
      loadCampaign();
    }
  }, [id]);

  // Reseller picker (admins only): list active ERP resellers.
  useEffect(() => {
    if (!profileData?.isAdmin()) return;
    api
      .get(`${API_URLS.CAMPAIGNS}/_values/resellers`)
      .then((r) => setResellers(r.data as ResellerOption[]))
      .catch((err) => console.warn("Failed to load resellers:", err));
  }, [profileData]);

  // React Router keeps this component mounted when only :id changes.
  useEffect(() => {
    setActiveTab("details");
  }, [id]);

  useEffect(() => {
    if (!isEditMode && profileData && !profileData.isAdmin()) {
      setResellerErpRef("");
    }
  }, [profileData, isEditMode]);

  const searchCompounds = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.post(`${API_URLS.CATALOG}/_search`, {
        search_text: query,
        limit: 10,
        page: 1,
      });
      setSearchResults(response.data?.results || []);
    } catch (err) {
      console.warn("Search error:", err);
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCompounds(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Nome da campanha é obrigatório");
      return;
    }
    if (!startsAt || !endsAt) {
      setError("Datas de início e fim são obrigatórias");
      return;
    }
    if (new Date(startsAt) >= new Date(endsAt)) {
      setError("Data de início deve ser anterior à data de fim");
      return;
    }
    if (!isEditMode && new Date(startsAt).getTime() < Date.now() - 60_000) {
      setError("A data de início não pode estar no passado");
      return;
    }

    setLoading({
      message: isEditMode ? "A atualizar campanha..." : "A criar campanha...",
    });
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      };

      if (isGlobal) {
        body.is_global = true;
      } else if (
        profileData?.isAdmin() &&
        !isEditMode &&
        resellerErpRef.trim()
      ) {
        body.reseller_erp_ref = resellerErpRef.trim();
      }

      if (isEditMode) {
        await api.put(`${API_URLS.CAMPAIGNS}/${id}`, body);
        setSuccess("Campanha atualizada com sucesso!");
      } else {
        const response = await api.post(API_URLS.CAMPAIGNS, body);
        const created = response.data as Campaign;
        setCampaign(created);
        setSuccess("Campanha criada com sucesso!");
        navigate(`/campaigns/${created.id}?edit=true`, { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao guardar campanha");
    } finally {
      setLoading(null);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    setLoading({ message: "A alterar estado..." });
    try {
      await api.put(`${API_URLS.CAMPAIGNS}/${id}/status`, {
        status: newStatus,
      });
      setSuccess("Estado alterado com sucesso!");
      loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao alterar estado");
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm("Tem a certeza que deseja eliminar esta campanha?")) return;

    setLoading({ message: "A eliminar campanha..." });
    try {
      await api.delete(`${API_URLS.CAMPAIGNS}/${id}`);
      setSuccess("Campanha eliminada!");
      navigate("/campaigns", { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao eliminar campanha");
    } finally {
      setLoading(null);
    }
  };

  const buildItemBody = useCallback((): CampaignItemRequestBody | null => {
    const compoundId = editingItem?.compound_id ?? selectedCompoundId;
    if (!compoundId) return null;

    return buildCampaignItemRequest({
      compoundId,
      editingItemId: editingItem?.id ?? null,
      isAdmin: !!profileData?.isAdmin(),
      promoType: newPromoType,
      discountPct: newDiscountPct,
      discountValue: newDiscountValue,
      fixedPvp: newFixedPvp,
      discountTarget: newDiscountTarget as DiscountTarget,
      niposomSplitPct: newNiposomSplitPct,
    });
  }, [
    editingItem,
    selectedCompoundId,
    profileData,
    newPromoType,
    newDiscountPct,
    newDiscountValue,
    newFixedPvp,
    newDiscountTarget,
    newNiposomSplitPct,
  ]);

  const handleSubmitItem = async () => {
    const body = buildItemBody();
    if (!body || !id) return;

    setLoading({
      message: editingItem
        ? "A atualizar composto..."
        : "A adicionar compound...",
    });
    try {
      if (editingItem) {
        const response = await api.put(
          `${API_URLS.CAMPAIGNS}/${id}/items/${editingItem.id}`,
          body,
        );
        setItems(
          items.map((i) =>
            i.id === editingItem.id ? (response.data as CampaignItem) : i,
          ),
        );
        setSuccess("Composto atualizado!");
      } else {
        const response = await api.post(
          `${API_URLS.CAMPAIGNS}/${id}/items`,
          body,
        );
        setItems([...items, response.data as CampaignItem]);
        setSuccess("Compound adicionado à campanha!");
      }
      closeItemModal();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          (editingItem
            ? "Erro ao atualizar composto"
            : "Erro ao adicionar compound"),
      );
    } finally {
      setLoading(null);
    }
  };

  const openAddItemModal = () => {
    modalTriggerRef.current = document.activeElement as HTMLElement | null;
    resetAddForm();
    setEditingItem(null);
    setPreview(null);
    setPreviewError(null);
    setPreviewPending(false);
    setShowAddModal(true);
  };

  const openEditItemModal = (item: CampaignItem) => {
    modalTriggerRef.current = document.activeElement as HTMLElement | null;
    const form = campaignItemToFormState(item);
    setSelectedCompoundId(item.compound_id);
    setNewPromoType(form.promoType);
    setNewDiscountPct(form.discountPct);
    setNewDiscountValue(form.discountValue);
    setNewFixedPvp(form.fixedPvp);
    setNewDiscountTarget(form.discountTarget);
    setNewNiposomSplitPct(form.niposomSplitPct);
    setSearchQuery("");
    setSearchResults([]);
    setEditingItem(item);
    setPreview(item);
    setPreviewError(null);
    setPreviewPending(false);
    setShowAddModal(true);
  };

  const closeItemModal = () => {
    setShowAddModal(false);
    setEditingItem(null);
    setPreview(null);
    setPreviewError(null);
    setPreviewPending(false);
    resetAddForm();
    modalTriggerRef.current?.focus?.();
  };

  // Live margin preview: the backend recomputes promotional PVP / margins /
  // reseller price from the same configured-price code path used on save, so
  // what the operator sees here is exactly what gets persisted (no conf/ERP drift).
  useEffect(() => {
    if (!showAddModal || !id) return;
    const body = buildItemBody();
    if (!body) {
      setPreview(null);
      setPreviewError(null);
      setPreviewPending(false);
      return;
    }
    setPreviewError(null);
    setPreviewPending(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await api.post(
          `${API_URLS.CAMPAIGNS}/${id}/items/_preview`,
          body,
        );
        if (cancelled) return;
        setPreview(response.data as CampaignItem);
        setPreviewError(null);
        setPreviewPending(false);
      } catch (err: any) {
        if (cancelled) return;
        console.warn("Campaign item preview failed:", err);
        setPreviewPending(false);
        // Keep the last good preview; surface the reason instead of vanishing.
        setPreviewError(
          err.response?.data?.message || "Não foi possível pré-visualizar.",
        );
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showAddModal, id, buildItemBody]);

  const handleRemoveItem = async (itemId: number) => {
    if (!id) return;
    if (!confirm("Remover este compound da campanha?")) return;

    setLoading({ message: "A remover compound..." });
    try {
      await api.delete(`${API_URLS.CAMPAIGNS}/${id}/items/${itemId}`);
      setItems(items.filter((i) => i.id !== itemId));
      setSuccess("Compound removido!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao remover compound");
    } finally {
      setLoading(null);
    }
  };

  const handleRevalidate = async (item: CampaignItem) => {
    if (!id) return;
    const label = item.compound?.name || item.compound_id;
    if (
      !confirm(
        `Revalidar "${label}" com o preço base atual? O preço promocional será recalculado.`,
      )
    ) {
      return;
    }
    setRevalidatePendingId(item.id);
    try {
      await api.post(
        `${API_URLS.CAMPAIGNS}/${id}/items/${item.id}/_revalidate`,
      );
      // Re-fetch so the enriched other_campaigns / effective flags stay correct.
      await loadCampaign();
      setSuccess("Item revalidado com o preço base atual.");
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao revalidar item");
    } finally {
      setRevalidatePendingId(null);
    }
  };

  const resetAddForm = () => {
    setSelectedCompoundId(null);
    setNewPromoType("DISCOUNT_PERCENTAGE");
    setNewDiscountPct(null);
    setNewDiscountValue(null);
    setNewFixedPvp(null);
    setNewDiscountTarget("RESELLER_MARGIN");
    setNewNiposomSplitPct(50);
    setSearchQuery("");
    setSearchResults([]);
  };

  const calculatePromoPrice = (item: CampaignItem): string => {
    if (item.promotional_pvp_with_vat != null) {
      return eur.format(item.promotional_pvp_with_vat);
    }
    const basePvp = item.compound?.configured_price_pvp_with_vat;
    if (!basePvp) return "—";
    switch (item.promo_type) {
      case "DISCOUNT_PERCENTAGE":
        if (item.discount_percentage != null) {
          const discount = basePvp * (item.discount_percentage / 100);
          return eur.format(basePvp - discount);
        }
        return "—";
      case "DISCOUNT_VALUE":
        if (item.discount_value != null) {
          return eur.format(basePvp - item.discount_value);
        }
        return "—";
      case "FIXED_PVP":
        if (item.fixed_pvp != null) {
          return eur.format(item.fixed_pvp);
        }
        return "—";
      default:
        return "—";
    }
  };

  const isReadonly = isEditMode && (!canEdit || !isEditing);

  // Document-level so the trap also holds after a backdrop click moves focus to <body>.
  useEffect(() => {
    if (!showAddModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeItemModal();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Focus escaped (e.g. backdrop click moved it to <body>): pull it back in.
      if (!modalRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal]);

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.button
        whileHover={reduceMotion ? undefined : { x: -3 }}
        onClick={() => navigate("/campaigns")}
        className="flex items-center gap-1 text-blue-500 hover:text-blue-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </motion.button>

      <div className="flex items-center gap-3 mb-6">
        <Percent className="w-7 h-7 text-blue-500" />
        <h1 className="text-2xl font-bold">
          {isEditMode ? campaign?.name || "Campanha" : "Nova Campanha"}
        </h1>
      </div>

      {isEditMode && (
        <div
          role="tablist"
          aria-label="Secções da campanha"
          className="flex gap-1 border-b mb-4"
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            const order: Array<"details" | "history"> = profileData?.isAdmin()
              ? ["details", "history"]
              : ["details"];
            const idx = order.indexOf(activeTab);
            const next =
              e.key === "ArrowRight"
                ? (idx + 1) % order.length
                : (idx - 1 + order.length) % order.length;
            e.preventDefault();
            setActiveTab(order[next]);
            tabRefs.current[order[next]]?.focus();
          }}
        >
          <button
            role="tab"
            id="tab-details"
            aria-controls="tabpanel-details"
            ref={(el) => {
              tabRefs.current["details"] = el;
            }}
            aria-selected={activeTab === "details"}
            tabIndex={activeTab === "details" ? 0 : -1}
            onClick={() => setActiveTab("details")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === "details"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Detalhes
          </button>
          {profileData?.isAdmin() && (
            <button
              role="tab"
              id="tab-history"
              aria-controls="tabpanel-history"
              ref={(el) => {
                tabRefs.current["history"] = el;
              }}
              aria-selected={activeTab === "history"}
              tabIndex={activeTab === "history" ? 0 : -1}
              onClick={() => setActiveTab("history")}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === "history"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Histórico
            </button>
          )}
        </div>
      )}

      {isEditMode && id && profileData?.isAdmin() && (
        <div
          role="tabpanel"
          id="tabpanel-history"
          aria-labelledby="tab-history"
          hidden={activeTab !== "history"}
        >
          <CampaignHistoryTab
            campaignId={id}
            active={activeTab === "history"}
          />
        </div>
      )}

      <div
        role={isEditMode ? "tabpanel" : undefined}
        id={isEditMode ? "tabpanel-details" : undefined}
        aria-labelledby={isEditMode ? "tab-details" : undefined}
        hidden={isEditMode ? activeTab !== "details" : undefined}
      >
        {isEditMode && (
          <div className="flex gap-2 mb-4">
            {campaign?.status !== "ARCHIVED" && canEdit && (
              <>
                {campaign?.status === "DRAFT" && (
                  <button
                    onClick={() => handleStatusChange("ACTIVE")}
                    className="bg-green-700 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-800"
                  >
                    Ativar
                  </button>
                )}
                {campaign?.status === "ACTIVE" && (
                  <>
                    <button
                      onClick={() => handleStatusChange("DRAFT")}
                      className="bg-amber-700 text-white px-3 py-1 rounded-lg text-sm hover:bg-amber-800"
                    >
                      Voltar a Rascunho
                    </button>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            "Tem a certeza que deseja arquivar esta campanha? Já não estará ativa e não pode ser reativada.",
                          )
                        ) {
                          handleStatusChange("ARCHIVED");
                        }
                      }}
                      className="border border-gray-300 text-gray-700 px-3 py-1 rounded-lg text-sm hover:bg-gray-100"
                    >
                      Arquivar
                    </button>
                  </>
                )}
              </>
            )}
            {campaign?.status === "DRAFT" &&
              canEdit &&
              !campaign?.has_history && (
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1 text-red-700 border border-red-200 px-3 py-1 rounded-lg text-sm hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-2">
              <label
                htmlFor="campaign-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nome
              </label>
              <input
                id="campaign-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isReadonly}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                placeholder="Ex: Campanha Verão 2025"
              />
            </div>
            {profileData?.isAdmin() && !isEditMode && (
              <div className="mb-4 col-span-full">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isGlobal}
                    onChange={(e) => setIsGlobal(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Campanha Global (aplica-se a todos os resellers)
                  </span>
                </label>
              </div>
            )}
            {!isGlobal && (
              <div className="md:col-span-2">
                <label
                  htmlFor="campaign-reseller"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reseller (ERP Ref)
                </label>
                {profileData?.isAdmin() && !isEditMode ? (
                  <ResellerCombobox
                    id="campaign-reseller"
                    value={resellerErpRef}
                    onChange={setResellerErpRef}
                    options={resellers}
                    placeholder={
                      resellers.length > 0
                        ? `Procurar entre ${resellers.length} resellers…`
                        : "A carregar resellers…"
                    }
                  />
                ) : (
                  <input
                    id="campaign-reseller"
                    type="text"
                    value={resellerErpRef}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                    placeholder="Automático (seu perfil)"
                  />
                )}
              </div>
            )}
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="campaign-starts-at"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Data de início
                </label>
                <input
                  id="campaign-starts-at"
                  type="datetime-local"
                  value={startsAt}
                  min={minStart}
                  onChange={(e) => setStartsAt(e.target.value)}
                  disabled={isReadonly}
                  className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label
                  htmlFor="campaign-ends-at"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Data de fim
                </label>
                <input
                  id="campaign-ends-at"
                  type="datetime-local"
                  value={endsAt}
                  min={minEnd}
                  onChange={(e) => setEndsAt(e.target.value)}
                  disabled={isReadonly}
                  className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="campaign-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Descrição
            </label>
            <textarea
              id="campaign-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isReadonly}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
              rows={3}
              placeholder="Descrição opcional da campanha"
            />
          </div>

          {!isReadonly && (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                {isEditMode ? "Guardar Alterações" : "Criar Campanha"}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              Compostos na Campanha ({items.length})
            </h2>
            {isEditMode &&
              canEdit &&
              !isReadonly &&
              campaign?.status === "DRAFT" && (
                <button
                  onClick={openAddItemModal}
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar Composto
                </button>
              )}
          </div>

          {items.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nenhum composto adicionado a esta campanha.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table
                className="w-full min-w-[52rem] text-sm"
                aria-label="Compostos na campanha"
              >
                <thead>
                  <tr className="border-b text-left">
                    <th scope="col" className="pb-2 font-medium w-11">
                      <span className="sr-only">Detalhes</span>
                    </th>
                    <th scope="col" className="pb-2 font-medium">
                      Composto
                    </th>
                    <th scope="col" className="pb-2 font-medium text-right">
                      PVP Normal
                    </th>
                    <th scope="col" className="pb-2 font-medium">
                      Tipo
                    </th>
                    {profileData?.isAdmin() && (
                      <th scope="col" className="pb-2 font-medium">
                        Alvo
                      </th>
                    )}
                    <th scope="col" className="pb-2 font-medium text-right">
                      Valor
                    </th>
                    <th scope="col" className="pb-2 font-medium text-right">
                      PVP Promocional
                    </th>
                    {canEdit && !isReadonly && campaign?.status === "DRAFT" && (
                      <th scope="col" className="pb-2 font-medium">
                        Ações
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const open = expandedItemId === item.id;
                    return (
                      <React.Fragment key={item.id}>
                        <tr className="border-b">
                          <td className="py-2">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedItemId(open ? null : item.id)
                              }
                              aria-expanded={open}
                              aria-controls={`erp-panel-${item.id}`}
                              aria-label={`Detalhes ERP e margens de ${item.compound?.name || item.compound_id}`}
                              className="min-h-11 min-w-11 p-2 text-gray-400 hover:text-blue-500 rounded-lg"
                            >
                              <ChevronRight
                                className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`}
                                aria-hidden="true"
                              />
                            </button>
                          </td>
                          <th
                            scope="row"
                            className="py-2 font-normal text-left"
                          >
                            <div className="flex items-center gap-2">
                              {item.compound?.image && (
                                <img
                                  src={item.compound.image}
                                  alt=""
                                  className="w-8 h-8 object-contain rounded"
                                />
                              )}
                              <span>
                                {item.compound?.name || item.compound_id}
                              </span>
                              <InvalidatedBadge item={item} />
                              {item.invalidated &&
                                canEdit &&
                                !isReadonly &&
                                campaign?.status !== "ARCHIVED" &&
                                (profileData?.isAdmin() ||
                                  !item.niposom_funded) && (
                                  <RevalidateButton
                                    item={item}
                                    pending={revalidatePendingId === item.id}
                                    onRevalidate={handleRevalidate}
                                  />
                                )}
                            </div>
                          </th>
                          <td className="py-2 text-right tabular-nums">
                            {item.compound?.configured_price_pvp_with_vat !=
                            null
                              ? eur.format(
                                  item.compound.configured_price_pvp_with_vat,
                                )
                              : "—"}
                          </td>
                          <td className="py-2">
                            {PROMO_TYPE_LABELS[item.promo_type] ||
                              item.promo_type}
                          </td>
                          {profileData?.isAdmin() && (
                            <td className="py-2">
                              {item.discount_target
                                ? DISCOUNT_TARGET_LABELS[item.discount_target] +
                                  (item.discount_target === "BOTH" &&
                                  item.niposom_split_pct != null
                                    ? ` · Niposom ${item.niposom_split_pct}% / Reseller ${100 - item.niposom_split_pct}%`
                                    : "")
                                : "—"}
                            </td>
                          )}
                          <td className="py-2 text-right tabular-nums">
                            {item.promo_type === "DISCOUNT_PERCENTAGE" &&
                            item.discount_percentage != null
                              ? `${item.discount_percentage}%`
                              : item.promo_type === "DISCOUNT_VALUE" &&
                                  item.discount_value != null
                                ? eur.format(item.discount_value)
                                : item.promo_type === "FIXED_PVP" &&
                                    item.fixed_pvp != null
                                  ? eur.format(item.fixed_pvp)
                                  : "—"}
                          </td>
                          <td className="py-2 font-semibold text-green-700 text-right tabular-nums">
                            {calculatePromoPrice(item)}
                          </td>
                          {canEdit &&
                            !isReadonly &&
                            campaign?.status === "DRAFT" && (
                              <td className="py-2">
                                <div className="flex items-center gap-1">
                                  {(profileData?.isAdmin() ||
                                    !item.niposom_funded) && (
                                    <>
                                      <button
                                        onClick={() => openEditItemModal(item)}
                                        className="min-h-11 min-w-11 inline-flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                        title="Editar desconto e alvo"
                                        aria-label={`Editar desconto de ${item.compound?.name || item.compound_id}`}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleRemoveItem(item.id)
                                        }
                                        className="min-h-11 min-w-11 inline-flex items-center justify-center text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                        title="Remover"
                                        aria-label={`Remover ${item.compound?.name || item.compound_id} da campanha`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                        </tr>
                        {open && (
                          <tr className="border-b bg-gray-50">
                            <td
                              id={`erp-panel-${item.id}`}
                              colSpan={
                                6 +
                                (profileData?.isAdmin() ? 1 : 0) +
                                (canEdit &&
                                !isReadonly &&
                                campaign?.status === "DRAFT"
                                  ? 1
                                  : 0)
                              }
                              className="p-4"
                            >
                              <CampaignItemErpPanel
                                item={item}
                                isAdmin={!!profileData?.isAdmin()}
                              />
                              <OtherCampaigns item={item} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-compound-modal-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.18 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: reduceMotion ? 0 : 0.2 }}
              className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-xl max-h-[90dvh] overflow-y-auto"
            >
              <h3
                id="add-compound-modal-title"
                className="text-lg font-semibold mb-4"
              >
                {editingItem ? "Editar Composto" : "Adicionar Composto"}
              </h3>

              {!editingItem && (
                <div className="mb-4">
                  <label
                    htmlFor="compound-search-input"
                    className="block text-sm font-medium mb-1"
                  >
                    Procurar Composto
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="compound-search-input"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-controls="compound-search-results"
                      aria-autocomplete="list"
                      className="flex-1 border rounded-lg px-3 py-2"
                      placeholder="Nome do composto..."
                    />
                  </div>
                  <p className="sr-only" aria-live="polite">
                    {searchQuery ? `${searchResults.length} resultados` : ""}
                  </p>
                  <div>
                    {searchResults.length > 0 && (
                      <div
                        className="mt-2 border rounded-lg max-h-40 overflow-y-auto"
                        id="compound-search-results"
                        role="listbox"
                        aria-label="Resultados da pesquisa"
                      >
                        {searchResults.map((compound) => (
                          <div
                            key={compound.id}
                            role="option"
                            tabIndex={0}
                            aria-selected={selectedCompoundId === compound.id}
                            onClick={() => setSelectedCompoundId(compound.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedCompoundId(compound.id);
                              }
                            }}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              selectedCompoundId === compound.id
                                ? "bg-blue-100"
                                : ""
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {compound.image && (
                                <img
                                  src={compound.image}
                                  alt=""
                                  className="w-6 h-6 object-contain rounded"
                                />
                              )}
                              <div>
                                <p className="text-sm font-medium">
                                  {compound.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  PVP:{" "}
                                  {compound.price?.value != null
                                    ? eur.format(compound.price.value)
                                    : "—"}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label
                  htmlFor="promo-type-select"
                  className="block text-sm font-medium mb-1"
                >
                  Tipo de Promoção
                </label>
                <select
                  id="promo-type-select"
                  value={newPromoType}
                  onChange={(e) => setNewPromoType(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="DISCOUNT_PERCENTAGE">Desconto (%)</option>
                  <option value="DISCOUNT_VALUE">Desconto (€)</option>
                  <option value="FIXED_PVP">PVP Fixo</option>
                </select>
              </div>

              {profileData?.isAdmin() && (
                <div className="mb-4">
                  <fieldset className="rounded-lg border border-gray-200 p-1 inline-flex gap-1">
                    <legend className="sr-only">Alvo do desconto</legend>
                    {[
                      { id: "RESELLER_MARGIN", label: "Reseller" },
                      { id: "NIPOSOM_MARGIN", label: "Niposom" },
                      { id: "BOTH", label: "Ambas" },
                    ].map((a) => (
                      <label
                        key={a.id}
                        className={`cursor-pointer rounded-md px-3 py-2 min-h-11 flex items-center gap-2 focus-within:ring-2 focus-within:ring-blue-500 ${
                          newDiscountTarget === a.id
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-600"
                        }`}
                      >
                        <input
                          type="radio"
                          name="discount-target"
                          value={a.id}
                          checked={newDiscountTarget === a.id}
                          onChange={() => setNewDiscountTarget(a.id)}
                          className="sr-only"
                        />
                        {a.label}
                      </label>
                    ))}
                  </fieldset>
                  <p className="mt-1 text-xs text-gray-500">
                    A que margem o desconto se aplica. Niposom/Ambas baixam o
                    preço de compra do reseller.
                  </p>

                  {newDiscountTarget === "BOTH" && (
                    <div className="mt-2">
                      <label
                        htmlFor="split-niposom"
                        className="block text-sm font-medium mb-1"
                      >
                        Split do desconto — Niposom {newNiposomSplitPct}% ·
                        Reseller {100 - newNiposomSplitPct}%
                      </label>
                      <input
                        id="split-niposom"
                        type="range"
                        min={5}
                        max={100}
                        step={5}
                        value={newNiposomSplitPct}
                        onChange={(e) =>
                          setNewNiposomSplitPct(Number(e.target.value))
                        }
                        aria-label="Percentagem do desconto aplicada à margem Niposom"
                        aria-valuetext={`${newNiposomSplitPct} por cento Niposom, ${100 - newNiposomSplitPct} por cento Reseller`}
                        className="w-full accent-blue-500 min-h-11"
                      />
                    </div>
                  )}
                </div>
              )}

              {newPromoType === "DISCOUNT_PERCENTAGE" && (
                <div className="mb-4">
                  <label
                    htmlFor="discount-pct-input"
                    className="block text-sm font-medium mb-1"
                  >
                    Percentagem de desconto
                  </label>
                  <input
                    id="discount-pct-input"
                    type="number"
                    value={newDiscountPct ?? ""}
                    onChange={(e) =>
                      setNewDiscountPct(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Ex: 10"
                    min="0"
                    max="99.99"
                    step="0.01"
                  />
                </div>
              )}

              {newPromoType === "DISCOUNT_VALUE" && (
                <div className="mb-4">
                  <label
                    htmlFor="discount-value-input"
                    className="block text-sm font-medium mb-1"
                  >
                    Valor de desconto (€)
                  </label>
                  <input
                    id="discount-value-input"
                    type="number"
                    value={newDiscountValue ?? ""}
                    onChange={(e) =>
                      setNewDiscountValue(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Ex: 50"
                    min="0"
                    step="0.01"
                  />
                </div>
              )}

              {newPromoType === "FIXED_PVP" && (
                <div className="mb-4">
                  <label
                    htmlFor="fixed-pvp-input"
                    className="block text-sm font-medium mb-1"
                  >
                    PVP Fixo (€)
                  </label>
                  <input
                    id="fixed-pvp-input"
                    type="number"
                    value={newFixedPvp ?? ""}
                    onChange={(e) =>
                      setNewFixedPvp(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Ex: 999"
                    min="0.01"
                    step="0.01"
                  />
                </div>
              )}

              {(editingItem || selectedCompoundId != null) && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                  <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">
                    Pré-visualização
                    {previewPending ? " · A atualizar…" : ""}
                  </h4>
                  <p>
                    Composto selecionado:{" "}
                    {editingItem
                      ? editingItem.compound?.name || editingItem.compound_id
                      : (searchResults.find((r) => r.id === selectedCompoundId)
                          ?.name ?? preview?.compound?.name)}
                  </p>
                  {previewError && (
                    <p role="alert" className="mt-1 text-xs text-red-700">
                      {previewError}
                    </p>
                  )}
                  {preview?.compound?.configured_price_pvp_with_vat != null && (
                    <p className="mt-1" aria-live="polite">
                      PVP Normal:{" "}
                      {preview.promotional_pvp_with_vat != null &&
                      preview.promotional_pvp_with_vat !==
                        preview.compound.configured_price_pvp_with_vat ? (
                        <>
                          <span className="line-through">
                            {eur.format(
                              preview.compound.configured_price_pvp_with_vat,
                            )}
                          </span>
                          {" → "}
                          <span className="text-green-700 font-semibold">
                            {eur.format(preview.promotional_pvp_with_vat)}
                          </span>
                        </>
                      ) : (
                        eur.format(
                          preview.compound.configured_price_pvp_with_vat,
                        )
                      )}
                    </p>
                  )}
                  {preview?.compound && (
                    <div
                      className={`mt-3 border-t pt-3 transition-opacity motion-reduce:transition-none ${
                        previewPending ? "opacity-60" : ""
                      }`}
                    >
                      <CampaignItemErpPanel
                        item={preview}
                        isAdmin={!!profileData?.isAdmin()}
                        showErpComparison={false}
                      />
                    </div>
                  )}
                  {preview?.compound &&
                    ((preview.compound.reseller_promo_margin_value != null &&
                      preview.compound.reseller_promo_margin_value < 0) ||
                      (profileData?.isAdmin() &&
                        preview.compound.niposom_promo_margin_value != null &&
                        preview.compound.niposom_promo_margin_value < 0)) && (
                      <p role="alert" className="mt-2 text-xs text-red-700">
                        Atenção: com este desconto há margem negativa. Revê o
                        valor ou o alvo.
                      </p>
                    )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={closeItemModal}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitItem}
                  disabled={editingItem ? false : selectedCompoundId == null}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {editingItem ? "Guardar Alterações" : "Adicionar"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignDetailPage;
